/**
 * API Key Delete Dialog Component
 *
 * Confirmation dialog for deactivating an API key.
 * Sets isActive=false on the key.
 */

import { useState } from 'react';
import { Trash2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useDeactivateApiKey } from '@/hooks/use-api-keys';
import { useToast } from '@/hooks/use-toast';
import type { ApiKey } from '@/types/api-key';

interface ApiKeyDeleteDialogProps {
  apiKey: ApiKey | null;
  onClose: () => void;
}

/**
 * API Key Delete Dialog
 * Renders confirmation dialog for deactivating an API key
 */
export function ApiKeyDeleteDialog({ apiKey, onClose }: ApiKeyDeleteDialogProps) {
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const deactivateApiKeyMutation = useDeactivateApiKey();

  const handleDelete = async () => {
    if (!apiKey) return;

    setIsDeleting(true);
    try {
      await deactivateApiKeyMutation.mutateAsync(apiKey.id);

      toast({
        title: 'API Key Deactivated',
        description: `${apiKey.keyName} has been deactivated`,
      });

      onClose();
    } catch (error) {
      toast({
        title: 'Deactivation Failed',
        description: error instanceof Error ? error.message : 'Failed to deactivate API key',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={apiKey !== null} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            Deactivate API Key
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to deactivate this API key? This will immediately prevent any
            requests using this key from authenticating.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted p-4 rounded-md space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Key Name:</span>
              <span className="font-medium">{apiKey?.keyName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Client Type:</span>
              <span className="font-medium">{apiKey?.clientType.toUpperCase()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Rate Limit:</span>
              <span className="font-mono text-xs">
                {apiKey?.maxRequests.toLocaleString()} /{' '}
                {Math.round((apiKey?.windowMs || 0) / 60000)} min
              </span>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            Note: This action will set the key&apos;s status to inactive. The key record will remain
            in the database for historical purposes.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deactivating...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Deactivate Key
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
